import type { NetworkFilterSchema } from "@/lib/api/model";

export type NetworkSource = "connections" | "overlaps";

/** Canonical order of `sources`; also the backend default (both). */
export const NETWORK_SOURCES: readonly NetworkSource[] = ["connections", "overlaps"];

/** Backend cap on `member_ids` (`NetworkFilterSchema`). */
export const MAX_NETWORK_MEMBERS = 50;

/**
 * The filter as the backend fills it on read (`NetworkFilterSchema`): both
 * sources, every member, any overlap. Turning the filter on writes this, so a
 * server round-trip compares equal to the draft.
 */
export const NETWORK_FILTER_DEFAULT = {
  sources: ["connections", "overlaps"],
  member_ids: [],
  min_strength: "low",
} as const satisfies NetworkFilterSchema;

/**
 * `value` without the keys at their backend default (`mode` "include"
 * included, like the backend's canonical form); `undefined` when there is no
 * filter. An empty object is still a filter (anyone the team knows), so it
 * is kept.
 */
export function canonicalNetworkFilter(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const out: Record<string, unknown> = { ...(value as Record<string, unknown>) };
  if (out.mode === "include") delete out.mode;
  if (JSON.stringify(out.sources) === JSON.stringify(NETWORK_FILTER_DEFAULT.sources)) {
    delete out.sources;
  }
  if (out.min_strength === NETWORK_FILTER_DEFAULT.min_strength) delete out.min_strength;
  return out;
}

/** `sources` with `source` toggled, in canonical order; never empty (the backend needs one). */
export function toggleSource(
  sources: readonly NetworkSource[] | undefined,
  source: NetworkSource,
  on: boolean,
): NetworkSource[] {
  const current = new Set(sources ?? NETWORK_SOURCES);
  if (on) current.add(source);
  else current.delete(source);
  const next = NETWORK_SOURCES.filter((s) => current.has(s));
  return next.length ? next : [...(sources ?? NETWORK_SOURCES)];
}
